import {useState, useEffect} from 'react';
import {useSelector} from 'react-redux';

import classNames from 'classnames';

import {getIsMainSidebarCollapsed} from 'nutshell-core/session/session-selectors';

import {PAGE_CONTAINER_CONTENT_ID} from '../../layout/page-container';

import {isFormTabSelected} from '../redux/consts';
import {
    getCoreEntityPageCreatorForm,
    getCoreEntityPageActionBarTab,
} from '../redux/core-entity-page-selectors';

import styles from './mini-header.module.css';

export const MINI_HEADER_HEIGHT_WITH_FORM = 236;
export const MINI_HEADER_HEIGHT_WITHOUT_FORM = 98;
export const MINI_HEADER_PEEP_HEIGHT_WITH_FORM = 252;

type MiniHeaderEntityType = 'lead' | 'peep';

interface MiniHeaderProps {
    isShown: boolean;
    contentRef: React.RefObject<HTMLDivElement>;
    scrollContainerTop: number;
    entityType: MiniHeaderEntityType;
    children: React.ReactNode;
}

export function MiniHeader(props: MiniHeaderProps) {
    const isMainSidebarCollapsed = useSelector(getIsMainSidebarCollapsed);

    const [miniHeaderStyles, setMiniHeaderStyles] = useState<{
        left: number;
        width: number;
        top: number;
    }>({left: 0, width: 0, top: props.scrollContainerTop});

    const actionBarTab = useSelector(getCoreEntityPageActionBarTab);
    const {isActive: creatorFormIsActive} = useSelector(getCoreEntityPageCreatorForm);
    const isFormOpen = isFormTabSelected(actionBarTab) || creatorFormIsActive;

    const formHeightByEntityType: Record<MiniHeaderEntityType, number> = {
        lead: MINI_HEADER_HEIGHT_WITH_FORM,
        peep: MINI_HEADER_PEEP_HEIGHT_WITH_FORM,
    };
    const currentHeight = isFormOpen
        ? formHeightByEntityType[props.entityType]
        : MINI_HEADER_HEIGHT_WITHOUT_FORM;

    useEffect(() => {
        const contentEl = props.contentRef.current;
        if (!contentEl) {
            return;
        }

        // The mini-header is position:fixed, but getBoundingClientRect().left
        // shifts when the page is horizontally scrolled. Grab the scroll parent
        // so we can add back its scrollLeft to calculate the correct position.
        const horizontalScrollParent = document.getElementById(PAGE_CONTAINER_CONTENT_ID);

        const updateMiniHeaderPosition = () => {
            const rect = contentEl.getBoundingClientRect();

            setMiniHeaderStyles({
                left: rect.left + (horizontalScrollParent?.scrollLeft ?? 0),
                width: rect.width,
                top: props.scrollContainerTop,
            });
        };

        updateMiniHeaderPosition();

        const resizeObserver = new ResizeObserver(() => {
            updateMiniHeaderPosition();
        });

        resizeObserver.observe(contentEl);

        return () => {
            resizeObserver.disconnect();
        };
    }, [props.contentRef, props.scrollContainerTop, isMainSidebarCollapsed]);

    return (
        <div
            style={{
                left: `${miniHeaderStyles.left}px`,
                width: `${miniHeaderStyles.width}px`,
                top: `${miniHeaderStyles.top}px`,
                maxHeight: props.isShown ? currentHeight : 0,
            }}
            className={classNames(styles.container, {
                [styles['container--scrolled-past-checkpoint']]: props.isShown,
            })}
        >
            {props.children}
        </div>
    );
}
